import { useId } from "react";
import { Icon } from "./utils/Status.helpers";
import { STATUS_CONFIG, STATUS_SIZE } from "./config/Status.config";
import { StatusOverflow } from "./common";
import "./Status.scss";

// ============================================================
//  StatusDot — minimal indicator (dot + optional icon + label)
//  Props: status, size ("sm"|"md"|"lg"), showLabel, showIcon,
//         icon (any Iconify icon name — overrides the default)
// ============================================================
export const StatusDot = ({
    status,
    size = STATUS_SIZE.MD,
    showLabel = true,
    showIcon = false,
    iconName,
    tooltipId,
    maxWidth,
    onClick,
    DEFAULT,
    className = "",
    style = {},
    ...otherIconConfig
}) => {
    const generatedId = useId().replace(/:/g, "");
    const targetId = tooltipId || `status-dot-${generatedId}`;

    const uppercaseNaming = status?.replace(/\s+/g, "").toUpperCase();
    const cfg = STATUS_CONFIG[uppercaseNaming] || DEFAULT || otherIconConfig.DEFAULT;
    if (!cfg) return null;

    // icon prop overrides STATUS_CONFIG default; falls back to config icon
    const resolvedIcon = iconName || cfg.iconName;

    const s = {
        xs: { dot: 6, fs: "10px", gap: "5px", iconSize: 11 },
        sm: { dot: 7, fs: "11px", gap: "6px", iconSize: 12 },
        md: { dot: 9, fs: "12px", gap: "7px", iconSize: 14 },
        lg: { dot: 11, fs: "13px", gap: "8px", iconSize: 16 },
        xl: { dot: 12, fs: "14px", gap: "9px", iconSize: 17 },
    }[size] || { dot: 9, fs: "12px", gap: "7px", iconSize: 14 };

    return (
        <span
            id={targetId}
            className={`status-dot ${className}`}
            onClick={onClick}
            style={{
                display: "inline-flex",
                alignItems: "center",
                maxWidth: maxWidth || "100%",
                minWidth: 0,
                boxSizing: "border-box",
                cursor: onClick ? "pointer" : "default",
                "--status-fs": s.fs,
                "--status-gap": s.gap,
                ...style,
            }}
        >
            <span
                className={cfg.pulse ? "ss-pulse" : ""}
                style={{
                    width: s.dot,
                    height: s.dot,
                    borderRadius: "50%",
                    background: cfg.dot,
                    flexShrink: 0,
                    display: "block",
                    color: cfg.dot,
                    lineHeight: 1.5,
                }}
            />

            {/* icon: custom prop → config default → nothing if showIcon=false */}
            {showIcon && (
                <Icon
                    iconName={resolvedIcon}
                    size={s.iconSize}
                    style={{ flexShrink: 0 }}
                    {...otherIconConfig}
                />
            )}

            {showLabel && (
                <StatusOverflow
                    label={cfg.label}
                    targetId={targetId}
                    style={{ fontSize: "inherit", fontWeight: 600, color: cfg.color }}
                />
            )}
        </span>
    );
};